import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto, GalleryDto } from '@nusign/picflow-backend-types';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { HANDLE_META } from '../handle-meta';
import { ActivityItem } from '../interfaces/activity.interface';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class UnseenActivityService {
  private assetGalleryLink: Map<string, Set<string>> = new Map();
  seenMap: Map<string, boolean> = new Map();

  public unseenActivityChange$ = new BehaviorSubject<number>(null);

  private config = inject(AppConfigService);
  private state = inject(StateService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private handleMeta = inject(HANDLE_META);

  public async loadUnseenTenantActivityCount(): Promise<void> {
    this.loader.remove('unseen_activity_tenant_count');
    this.state.unseenActivity = await this.getUnseenTenantActivityCount();
    this.unseenActivityChange$.next(this.state.unseenActivity);
    this.handleMeta();
  }

  public async getUnseenTenantActivityCount() {
    return this.loader.get('unseen_activity_tenant_count', () => this.getUnseenTenantActivityCountInternal());
  }

  private async getUnseenTenantActivityCountInternal() {
    const url = `${this.config.apiEndpoint}/customer_activities?limit=1&include[]=count_unseen`;
    const resp = await lastValueFrom(this.http.get<{ count_unseen: number }>(url));
    return resp.count_unseen;
  }

  public updateUnseen(item: ActivityItem, seen: boolean) {
    const change = seen ? -1 : 1;
    item.customer_seen_status = seen;
    const assetId = item.context.assetId;
    const galleryId = item.context.gallery?.id;

    if (assetId) {
      if (!this.state.unseenActivityCountMap.has(assetId)) this.state.unseenActivityCountMap.set(assetId, 0);
      const newVal = Math.max(this.state.unseenActivityCountMap.get(assetId) + change, 0);
      this.state.unseenActivityCountMap.set(assetId, newVal);
    }
    if (galleryId && this.state.unseenActivityCountMap.has(galleryId)) {
      const newVal = Math.max(this.state.unseenActivityCountMap.get(galleryId) + change, 0);
      this.state.unseenActivityCountMap.set(galleryId, newVal);
    }

    this.state.unseenActivity += change;
    this.unseenActivityChange$.next(change);
    this.handleMeta();
  }

  public async markAsSeen(userActivityItems: ActivityItem[]) {
    if (!userActivityItems.length) return;
    userActivityItems.forEach(item => this.updateUnseen(item, true));

    const data = userActivityItems.map(item => ({
      op: 'replace',
      path: `/user_activity/${item.customer_activity_id}/seen`,
      value: true,
    }));
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/customer_activities`, data));
  }

  public async markTenantAsSeen() {
    if (this.state.impersonating) {
      return console.warn('Not marking tenant as seen because we are impersonating.');
    }
    const data = { tenant: this.state.activeTenant.id };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/customer_activities/mark_seen`, data));
    this.state.unseenActivity = 0;
    this.state.unseenActivityCountMap.clear();
    this.handleMeta();
    this.loader.remove('unseen_activity_tenant_count');
    this.unseenActivityChange$.next(-Infinity);
  }

  public async markGalleryAsSeen(gallery: GalleryDto) {
    if (this.state.impersonating) {
      return console.warn('Not marking gallery as seen because we are impersonating.');
    }
    const data = { gallery: gallery.id };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/customer_activities/mark_seen`, data));

    if (this.state.unseenActivityCountMap.has(gallery.id)) {
      this.state.unseenActivity -= this.state.unseenActivityCountMap.get(gallery.id);
    }

    // Setting the unseen count of this gallery to 0
    this.state.unseenActivityCountMap.set(gallery.id, 0);

    // Finding all the assets that belong to this gallery and for each of those,
    // finding all the activity items that we have and updating their seenMap value to true
    const assets = this.assetGalleryLink.get(gallery.id);
    const ids = [gallery.id];
    if (assets) {
      assets.forEach(assetId => {
        this.state.unseenActivityCountMap.set(assetId, 0);
        ids.push(assetId);
      });
    }

    this.unseenActivityChange$.next(-Infinity);
    this.handleMeta();
    this.loader.remove('unseen_activity_tenant_count');
  }

  public async markAssetAsSeen(assset: AssetDto) {
    if (this.state.impersonating) {
      return console.warn('Not marking asset as seen because we are impersonating.');
    }
    const data = { asset: assset.id };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/customer_activities/mark_seen`, data));

    // Updating the unseen count of the gallery that this asset belongs to
    const value = this.state.unseenActivityCountMap.get(assset.id) || 0;
    const currentGalleryValue = this.state.unseenActivityCountMap.get(<string>assset.gallery);
    if (currentGalleryValue) {
      this.state.unseenActivityCountMap.set(<string>assset.gallery, currentGalleryValue - value);
    }

    // Setting the unseen count of this asset to 0
    this.state.unseenActivityCountMap.set(assset.id, 0);

    // Finding all the activity items that we have and updating their seenMap value to true

    this.handleMeta();
    this.loader.remove('unseen_activity_tenant_count');
    this.unseenActivityChange$.next(-Infinity);
  }

  public registerAssets(asset: AssetDto) {
    const galleryId = <string>asset.gallery;
    const set = this.assetGalleryLink.get(galleryId) || new Set();
    set.add(asset.id);
    if (!this.assetGalleryLink.has(galleryId)) {
      this.assetGalleryLink.set(galleryId, set);
    }
  }
}
